import { useState, useMemo, useEffect, useCallback } from 'react';

import { useOpen } from 'hooks/use-open';

import { useAidResults } from '../../../hooks/use-aid-results';
import { useAidModelsList } from '../../../../hooks/use-aid-models';
import { formatMetricValue } from '../helpers/results-format';
import { COMPARISON_METRICS } from '../results-step.constants';

import type { AidModelDto, AidModelResultRowDto } from 'api/generated/schemas';
import type { MetricKind } from '../results-step.types';

export interface ComparisonMetric {
  key: string;
  value: string;
  kind: MetricKind;
}

export interface ComparisonRow {
  versionId: number | undefined;
  versionLabel: string;
  isDisabled: boolean;
  metrics: ComparisonMetric[];
}

export interface VersionOption {
  label: string;
  value: number;
}

export interface ComparisonRowState {
  id: string;
  selectedVersionId: number | undefined;
}

interface UseCompareEstimatesParams {
  userId: string;
  clientId: string | number;
  modelId: string | undefined;
  currentModelName: string | undefined;
}

export interface UseCompareEstimatesResult {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  canCompare: boolean;
  currentRow: ComparisonRow;
  comparisonRowStates: ComparisonRowState[];
  versionOptions: VersionOption[];
  setRowVersionId: (rowId: string, versionId: number | undefined) => void;
  addRow: () => void;
  removeRow: (rowId: string) => void;
  canAddRow: boolean;
  userId: string;
}

const STORAGE_KEY_PREFIX = 'compare-estimates-';

const COMPARABLE_MODELS_PAGE_SIZE = 100;

const getStorageKey = (modelId: string | undefined): string =>
  `${STORAGE_KEY_PREFIX}${modelId ?? 'unknown'}`;

let rowIdCounter = 0;
const generateRowId = (): string => {
  rowIdCounter += 1;

  return `row-${Date.now()}-${rowIdCounter}`;
};

const createDefaultRowStates = (): ComparisonRowState[] => [
  { id: generateRowId(), selectedVersionId: undefined },
];

export const buildMetrics = (
  totalFirstYear: AidModelResultRowDto | undefined
): ComparisonMetric[] =>
  COMPARISON_METRICS.map((metric) => {
    const raw = metric.field ? totalFirstYear?.[metric.field] : null;
    const value = typeof raw === 'number' ? raw : null;

    return {
      key: metric.key,
      value: formatMetricValue(value, metric.kind),
      kind: metric.kind,
    };
  });

export const getVersionLabel = (version: AidModelDto): string =>
  version.name ?? `Model ${version.id}`;

const saveToSessionStorage = (
  modelId: string | undefined,
  rowStates: ComparisonRowState[]
): void => {
  try {
    const data = rowStates.map((r) => r.selectedVersionId ?? null);

    sessionStorage.setItem(getStorageKey(modelId), JSON.stringify(data));
  } catch (error) {
    console.error('Failed to save comparison state to sessionStorage', error);
  }
};

const loadFromSessionStorage = (
  modelId: string | undefined
): ComparisonRowState[] | null => {
  try {
    const raw = sessionStorage.getItem(getStorageKey(modelId));

    if (!raw) return null;
    const parsed = JSON.parse(raw) as (number | null)[];

    if (!Array.isArray(parsed) || !parsed.length) return null;

    return parsed.map((versionId) => ({
      id: generateRowId(),
      selectedVersionId: versionId ?? undefined,
    }));
  } catch {
    return null;
  }
};

export const useCompareEstimates = ({
  userId,
  clientId,
  modelId,
  currentModelName,
}: UseCompareEstimatesParams): UseCompareEstimatesResult => {
  const { isOpen, handleOpen, handleClose } = useOpen();
  const [comparisonRowStates, setComparisonRowStates] = useState<
    ComparisonRowState[]
  >(createDefaultRowStates);

  const modelsQuery = useAidModelsList({
    userId,
    clientId,
    queryParams: { pageSize: COMPARABLE_MODELS_PAGE_SIZE },
  });

  const currentModelIdNum = modelId ? Number(modelId) : undefined;

  const otherVersions = useMemo(() => {
    const raw = modelsQuery.data?.data ?? [];

    return raw
      .filter((model) => model.id !== currentModelIdNum)
      .sort((a, b) => {
        const dateA = a.createdDate ? new Date(a.createdDate).getTime() : 0;
        const dateB = b.createdDate ? new Date(b.createdDate).getTime() : 0;

        return dateB - dateA;
      });
  }, [modelsQuery.data, currentModelIdNum]);

  const hasVersions = !!otherVersions.length;

  const versionOptions: VersionOption[] = useMemo(
    () =>
      otherVersions.map((v) => ({
        label: getVersionLabel(v),
        value: v.id!,
      })),
    [otherVersions]
  );

  useEffect(() => {
    handleClose();

    const restored = loadFromSessionStorage(modelId);

    setComparisonRowStates(restored ?? createDefaultRowStates());
  }, [modelId]);

  useEffect(() => {
    if (!isOpen) return;

    const restored = loadFromSessionStorage(modelId);

    if (restored) {
      setComparisonRowStates(restored);
    }
  }, [isOpen, modelId]);

  useEffect(() => {
    if (isOpen) {
      saveToSessionStorage(modelId, comparisonRowStates);
    }
  }, [comparisonRowStates, isOpen, modelId]);

  const currentResults = useAidResults({
    userId,
    modelId: modelId ?? '',
    enabled: !!modelId && isOpen,
  });

  const currentRow: ComparisonRow = useMemo(
    () => ({
      versionId: currentModelIdNum,
      versionLabel: currentModelName ?? 'Current model',
      isDisabled: true,
      metrics: buildMetrics(
        currentResults.data?.estimatedResults?.totalFirstYear
      ),
    }),
    [currentModelIdNum, currentModelName, currentResults.data]
  );

  const setRowVersionId = useCallback(
    (rowId: string, versionId: number | undefined) => {
      setComparisonRowStates((prev) =>
        prev.map((row) =>
          row.id === rowId ? { ...row, selectedVersionId: versionId } : row
        )
      );
    },
    []
  );

  const addRow = useCallback(() => {
    setComparisonRowStates((prev) => [
      ...prev,
      { id: generateRowId(), selectedVersionId: undefined },
    ]);
  }, []);

  const removeRow = useCallback((rowId: string) => {
    setComparisonRowStates((prev) => prev.filter((row) => row.id !== rowId));
  }, []);

  const canAddRow = comparisonRowStates.length < otherVersions.length;

  return {
    isOpen,
    open: handleOpen,
    close: handleClose,
    canCompare: hasVersions,
    currentRow,
    comparisonRowStates,
    versionOptions,
    setRowVersionId,
    addRow,
    removeRow,
    canAddRow,
    userId,
  };
};
